/**
 * LeftShield Portal - AI Governance Overview Page
 * Dashboard showing how LeftShield governs its own AI decisions.
 * Route: /ai-governance | Category: AI
 *
 * High-Risk Decisions Queue migrated onto the List & Grid Standard — see
 * `.github/context/list-grid-standard.context.md` and
 * `.github/context/list-grid-standard-behaviours.context.md`, following the
 * committed reference migration `src/pages/ApiKeysPage.tsx`. The rest of the
 * dashboard (summary tiles, pipeline, constitution health, trust trend,
 * simulation queue) is left as-is — not tabular lists.
 *
 * §18 GUID-routing fix (#12737): the queue is now backed by the real, GUID-native
 * `GET /api/portal/ai-governance/decisions` endpoint (`AiGovernanceController.cs` /
 * `IAiGovernanceService.AiDecisionDto`) instead of a hardcoded mock array — the detail
 * chevron routes on the real `Guid Id`. See `src/services/api.ts` → `aiGovernanceApi`.
 */
import React, { useEffect } from 'react';
import type { ReactNode } from 'react';
import {
  Title2,
  Title3,
  Body1,
  Body2,
  Caption1,
  Card,
  Badge,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  tokens,
  makeStyles,
} from '@fluentui/react-components';
import {
  BrainCircuitRegular,
  ShieldCheckmarkRegular,
  CheckmarkCircleRegular,
  WarningRegular,
  ArrowRightRegular,
  BeakerRegular,
  GavelRegular,
  DocumentCheckmarkRegular,
  ChevronRight24Regular,
  ArrowSort16Regular,
  ArrowSortUp16Regular,
  ArrowSortDown16Regular,
} from '@fluentui/react-icons';
import { useNavigate } from 'react-router-dom';
import { usePageStyles } from '../styles/page-styles';
import { PageHeader } from '../components/common/PageHeader';
import { governanceTokens } from '../components/design-system';
import { aiGovernanceApi } from '../services/api';
import type { AiDecision } from '../services/api';
import {
  ListToolbar,
  ListFrame,
  Pager,
  actionsColumnWidth,
  useListUrlState,
  useAdaptiveGridData,
  LoadingSkeleton,
  ErrorState,
  EmptyState,
  ListLoadingOverlay,
} from '../components/list';
import type { ListQueryParams, PagedResult, SortDirection, SortState } from '../components/list';

// ---------------------------------------------------------------------------
// Local styles
// ---------------------------------------------------------------------------

const AI_COLOR = '#8852E0';

const useLocalStyles = makeStyles({
  pipelineContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    overflowX: 'auto',
    padding: `${tokens.spacingVerticalM} 0`,
  },
  pipelineStep: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalXS,
    padding: tokens.spacingVerticalM,
    borderRadius: tokens.borderRadiusMedium,
    minWidth: '130px',
    flex: 1,
    textAlign: 'center' as const,
  },
  pipelineArrow: {
    fontSize: '20px',
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
  },
  barRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    minWidth: 0,
  },
  bar: {
    height: '8px',
    borderRadius: tokens.borderRadiusSmall,
  },
  trendGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: tokens.spacingHorizontalS,
    textAlign: 'center' as const,
  },
  trendBar: {
    borderRadius: tokens.borderRadiusSmall,
    minHeight: '8px',
  },
});

// ---------------------------------------------------------------------------
// Demo data
// ---------------------------------------------------------------------------

const SUMMARY_STATS = [
  { label: 'Total AI Decisions', value: '1,847', color: AI_COLOR },
  { label: 'Evidence-Verified', value: '94.2%', color: governanceTokens.colorStatusSuccess },
  { label: 'Unsupported', value: '1.8%', color: governanceTokens.colorStatusWarning },
  { label: 'Validation Pass Rate', value: '97.1%', color: governanceTokens.colorStatusSuccess },
  { label: 'Constitution Violations', value: '3', color: governanceTokens.colorStatusError },
  { label: 'Review Required', value: '12', color: governanceTokens.colorStatusWarning },
];

const PIPELINE_STEPS = [
  { label: 'Reasoning', count: 1847, color: AI_COLOR, icon: <BrainCircuitRegular /> },
  { label: 'Constitution Check', count: 1847, color: '#13B5B1', icon: <GavelRegular /> },
  { label: 'Evidence Support', count: 1740, color: '#E89652', icon: <DocumentCheckmarkRegular /> },
  { label: 'Validation', count: 1695, color: '#1FD2D3', icon: <ShieldCheckmarkRegular /> },
  { label: 'Final Status', count: 1695, color: governanceTokens.colorStatusSuccess, icon: <CheckmarkCircleRegular /> },
];

/** Fetches one page from the real GUID-native decisions ledger — `GET /api/portal/ai-governance/decisions`
 *  (`AiGovernanceController.ListDecisions`). The endpoint does not (yet) expose a `search` or `riskLevel`
 *  query param, so those are applied client-side by `useAdaptiveGridData` when the ledger is small enough
 *  to run in client mode (see `ADAPTIVE_GRID_THRESHOLD`) — mirrors `ApiKeysPage`'s adaptive-wrap pattern. */
async function fetchAiDecisionsPage(params: ListQueryParams): Promise<PagedResult<AiDecision>> {
  const result = await aiGovernanceApi.getDecisions({ page: params.page, pageSize: params.pageSize });
  const totalPages = Math.max(Math.ceil(result.totalCount / result.pageSize), 1);
  return {
    items: result.items,
    totalCount: result.totalCount,
    pageNumber: result.page,
    pageSize: result.pageSize,
    totalPages,
    hasPreviousPage: result.page > 1,
    hasNextPage: result.page < totalPages,
  };
}

const CONSTITUTION_HEALTH = {
  activeRules: 7,
  warnings: 2,
  violations: { critical: 1, high: 2, medium: 0, low: 0 },
};

const TRUST_TREND = [
  { day: 'Mon', verified: 241, unsupported: 6 },
  { day: 'Tue', verified: 258, unsupported: 5 },
  { day: 'Wed', verified: 262, unsupported: 4 },
  { day: 'Thu', verified: 249, unsupported: 5 },
  { day: 'Fri', verified: 271, unsupported: 4 },
  { day: 'Sat', verified: 198, unsupported: 3 },
  { day: 'Sun', verified: 261, unsupported: 3 },
];

interface SimulationItem {
  id: string;
  description: string;
  source: string;
  status: 'pending' | 'approved' | 'rejected';
}

const SIMULATION_QUEUE: SimulationItem[] = [
  { id: 'sim-001', description: 'Block builds with detected PII in logs', source: 'PolicyEngine', status: 'pending' },
  { id: 'sim-002', description: 'Auto-remediate critical CVEs in staging', source: 'FixPRAgent', status: 'pending' },
  { id: 'sim-003', description: 'Enforce MFA on all CI/CD service accounts', source: 'ComplianceAdvisor', status: 'pending' },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const riskBadgeColor = (level: string): 'danger' | 'warning' | 'success' | 'informative' => {
  switch (level.toLowerCase()) {
    case 'critical':
    case 'high':
      return 'danger';
    case 'medium':
      return 'warning';
    case 'low':
      return 'success';
    default:
      return 'informative';
  }
};

const severityBarWidth = (count: number, max: number): string =>
  max === 0 ? '0%' : `${Math.max((count / max) * 100, count > 0 ? 8 : 0)}%`;

const simulationStatusColor = (status: string): 'warning' | 'success' | 'danger' => {
  switch (status) {
    case 'approved': return 'success';
    case 'rejected': return 'danger';
    default: return 'warning';
  }
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const AIGovernanceOverviewPage: React.FC = () => {
  const ps = usePageStyles();
  const ls = useLocalStyles();
  const navigate = useNavigate();

  const maxTrustVerified = Math.max(...TRUST_TREND.map((d) => d.verified));

  // High-Risk Decisions Queue — List & Grid Standard (URL-backed page/sort/search state).
  const {
    state: decisionsState,
    setPage: setDecisionsPage,
    setSort: setDecisionsSort,
    setSearch: setDecisionsSearch,
  } = useListUrlState({ defaultSort: 'agentName:asc' });

  const [decisionsSortField, decisionsSortDirRaw] = decisionsState.sort!.split(':');
  const decisionsSortDir: SortDirection = decisionsSortDirRaw === 'desc' ? 'desc' : 'asc';
  const decisionsSortState: SortState[] = [{ field: decisionsSortField, dir: decisionsSortDir }];

  const toggleDecisionsSort = (field: string) => {
    if (field === decisionsSortField) {
      setDecisionsSort(`${field}:${decisionsSortDir === 'asc' ? 'desc' : 'asc'}`);
    } else {
      setDecisionsSort(`${field}:asc`);
    }
  };

  const decisionsSortIcon = (field: string) => {
    if (field !== decisionsSortField) return <ArrowSort16Regular />;
    return decisionsSortDir === 'asc' ? <ArrowSortUp16Regular /> : <ArrowSortDown16Regular />;
  };

  const decisionsGrid = useAdaptiveGridData<AiDecision>({
    queryKeyBase: ['ai-governance-decisions'],
    fetchPage: fetchAiDecisionsPage,
    page: decisionsState.page,
    pageSize: decisionsState.pageSize,
    search: decisionsState.search,
    searchFields: ['agentName', 'subjectId', 'decisionType'],
    sortState: decisionsSortState,
  });

  const {
    items: highRiskDecisions,
    totalCount: decisionsTotalCount,
    totalPages: decisionsTotalPages,
    hasPreviousPage: decisionsHasPreviousPage,
    hasNextPage: decisionsHasNextPage,
    isLoading: decisionsLoading,
    isFetching: decisionsFetching,
    isError: decisionsIsError,
    refetch: refetchDecisions,
  } = decisionsGrid;

  // Out-of-range clamp (§9) — never strand the user on an empty page after search/sort.
  useEffect(() => {
    if (!decisionsLoading && decisionsState.page > decisionsTotalPages) {
      setDecisionsPage(decisionsTotalPages);
    }
  }, [decisionsLoading, decisionsState.page, decisionsTotalPages, setDecisionsPage]);

  const decisionsActionButtonCount = 1; // chevron-only Actions cell (§18) — decisions have no per-row
  // mutation (approve/reject live on the Simulations endpoints, not Decisions — see
  // `AiGovernanceController.cs`), so this is a read-only queue, not role-gated.
  const decisionsColumnCount = 6;

  let decisionsBody: ReactNode;
  if (decisionsLoading) {
    decisionsBody = <LoadingSkeleton rows={5} columns={decisionsColumnCount} />;
  } else if (decisionsIsError) {
    decisionsBody = <ErrorState message="Unable to load high-risk decisions." onRetry={refetchDecisions} />;
  } else if (highRiskDecisions.length === 0) {
    decisionsBody = (
      <EmptyState
        icon={<WarningRegular />}
        title="No High-Risk Decisions"
        description="No high-risk AI decisions currently require review."
      />
    );
  } else {
    decisionsBody = (
      <ListLoadingOverlay busy={decisionsFetching && !decisionsLoading}>
        <Table aria-label="High-risk AI decisions">
          <TableHeader>
            <TableRow>
              <TableHeaderCell
                sortable
                sortDirection={
                  decisionsSortField === 'agentName' ? (decisionsSortDir === 'asc' ? 'ascending' : 'descending') : undefined
                }
                sortIcon={decisionsSortIcon('agentName')}
                onClick={() => toggleDecisionsSort('agentName')}
              >
                Agent
              </TableHeaderCell>
              <TableHeaderCell style={{ width: '100%' }}>Subject</TableHeaderCell>
              <TableHeaderCell>Risk Level</TableHeaderCell>
              <TableHeaderCell
                sortable
                sortDirection={
                  decisionsSortField === 'decisionType'
                    ? (decisionsSortDir === 'asc' ? 'ascending' : 'descending')
                    : undefined
                }
                sortIcon={decisionsSortIcon('decisionType')}
                onClick={() => toggleDecisionsSort('decisionType')}
              >
                Decision Type
              </TableHeaderCell>
              <TableHeaderCell>Timestamp</TableHeaderCell>
              <TableHeaderCell style={{ width: actionsColumnWidth(decisionsActionButtonCount) }}>
                Actions
              </TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {highRiskDecisions.map((d) => (
              <TableRow key={d.id}>
                <TableCell>
                  <Body1 style={{ fontWeight: 600 }}>
                    {d.agentName} <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>{d.agentVersion}</Caption1>
                  </Body1>
                </TableCell>
                <TableCell>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS }}>
                    <Body2 style={{ color: tokens.colorNeutralForeground3 }}>{d.subjectId}</Body2>
                    <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>{d.subjectType}</Caption1>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge appearance="tint" color={riskBadgeColor(d.riskLevel)} size="small">
                    {d.riskLevel}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge appearance="outline" size="small">{d.decisionType}</Badge>
                </TableCell>
                <TableCell>
                  <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                    {new Date(d.createdAt).toLocaleString()}
                  </Caption1>
                </TableCell>
                <TableCell>
                  {/* §18 detail chevron — read-only queue, no per-row mutation, so this is the
                      only row affordance. Not role-gated (viewing this queue has no role check).
                      Routes on the real GUID `Id` from `AiDecisionDto` (§18 fix — was a mock slug). */}
                  <Button
                    appearance="subtle"
                    icon={<ChevronRight24Regular />}
                    size="small"
                    aria-label="View decision details"
                    onClick={() => navigate(`/ai-governance/decisions/${d.id}`)}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ListLoadingOverlay>
    );
  }

  return (
    <div className={ps.page}>
      <PageHeader
        title="AI Governance"
        subtitle="LeftShield governs its own AI. Every decision is traced, validated, and constitution-checked before it reaches you."
        icon={<BrainCircuitRegular />}
        iconColor={AI_COLOR}
      />

      {/* A. AI Reliability Summary */}
      <Card>
        <div className={ps.cardBody}>
          <div className={ps.sectionHeader}>
            <Title2>AI Reliability Summary</Title2>
            <Badge appearance="outline" color="informative">Live</Badge>
          </div>

          <div className={ps.statGrid}>
            {SUMMARY_STATS.map((stat) => (
              <div key={stat.label} className={ps.statTile}>
                <Body2 style={{ color: tokens.colorNeutralForeground3 }}>{stat.label}</Body2>
                <Title2 style={{ color: stat.color }}>{stat.value}</Title2>
              </div>
            ))}
          </div>
        </div>
      </Card>

      {/* B. AI Decision Pipeline */}
      <Card>
        <div className={ps.cardBody}>
          <div className={ps.sectionHeader}>
            <Title2>AI Decision Pipeline</Title2>
            <Badge appearance="outline" color="brand">Sequential</Badge>
          </div>

          <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
            Every AI output passes through this pipeline before it is surfaced to users.
          </Body2>

          <div className={ls.pipelineContainer}>
            {PIPELINE_STEPS.map((step, idx) => (
              <React.Fragment key={step.label}>
                {idx > 0 && (
                  <span className={ls.pipelineArrow}>
                    <ArrowRightRegular />
                  </span>
                )}
                <div
                  className={ls.pipelineStep}
                  style={{
                    backgroundColor: tokens.colorNeutralBackground2,
                    borderLeft: `3px solid ${step.color}`,
                  }}
                >
                  <span style={{ fontSize: '24px', color: step.color }}>{step.icon}</span>
                  <Body2 style={{ fontWeight: 600 }}>{step.label}</Body2>
                  <Title3 className={ps.metricValue} style={{ color: step.color }}>
                    {step.count.toLocaleString()}
                  </Title3>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
      </Card>

      {/* C. High-Risk Decisions Queue — List & Grid Standard (§2 embedded ListFrame) */}
      <Card>
        <div className={ps.cardBody}>
          <div className={ps.sectionHeader}>
            <Title2>High-Risk Decisions Queue</Title2>
            <Badge appearance="tint" color="danger" icon={<WarningRegular />}>
              {decisionsTotalCount} items
            </Badge>
          </div>

          <ListToolbar
            search={decisionsState.search}
            onSearchChange={setDecisionsSearch}
            searchPlaceholder="Search high-risk decisions…"
            onRefresh={refetchDecisions}
            isRefreshing={decisionsFetching}
            testIdPrefix="ai-governance-decisions"
          />
          <ListFrame>{decisionsBody}</ListFrame>
          <Pager
            page={decisionsState.page}
            totalPages={decisionsTotalPages}
            hasPreviousPage={decisionsHasPreviousPage}
            hasNextPage={decisionsHasNextPage}
            totalCount={decisionsTotalCount}
            onPageChange={setDecisionsPage}
            testId="ai-governance-decisions-pager"
          />
        </div>
      </Card>

      <div className={ps.twoColumn}>
        {/* D. Constitution Health */}
        <Card>
          <div className={ps.cardBody}>
            <div className={ps.sectionHeader}>
              <Title2>Constitution Health</Title2>
              <Button
                appearance="subtle"
                size="small"
                onClick={() => navigate('/ai-governance/constitution')}
              >
                View Rules
              </Button>
            </div>

            <div className={ps.statGrid}>
              <div className={ps.statTile}>
                <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Active Rules</Body2>
                <Title2 style={{ color: AI_COLOR }}>{CONSTITUTION_HEALTH.activeRules}</Title2>
              </div>
              <div className={ps.statTile}>
                <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Warnings</Body2>
                <Title2 style={{ color: governanceTokens.colorStatusWarning }}>{CONSTITUTION_HEALTH.warnings}</Title2>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS }}>
              <Body2 style={{ fontWeight: 600 }}>Violations by Severity</Body2>
              {(['critical', 'high', 'medium', 'low'] as const).map((sev) => {
                const count = CONSTITUTION_HEALTH.violations[sev];
                const colors: Record<string, string> = {
                  critical: governanceTokens.colorStatusError,
                  high: '#E89652',
                  medium: governanceTokens.colorStatusWarning,
                  low: tokens.colorNeutralForeground3,
                };
                return (
                  <div key={sev} className={ls.barRow}>
                    <Caption1 style={{ width: '60px', textTransform: 'capitalize' }}>{sev}</Caption1>
                    <div style={{ flex: 1, backgroundColor: tokens.colorNeutralBackground2, borderRadius: tokens.borderRadiusSmall, height: '8px' }}>
                      <div
                        className={ls.bar}
                        style={{
                          width: severityBarWidth(count, 3),
                          backgroundColor: colors[sev],
                        }}
                      />
                    </div>
                    <Caption1 style={{ width: '20px', textAlign: 'right' }}>{count}</Caption1>
                  </div>
                );
              })}
            </div>
          </div>
        </Card>

        {/* E. AI Trust Trend */}
        <Card>
          <div className={ps.cardBody}>
            <div className={ps.sectionHeader}>
              <Title2>AI Trust Trend</Title2>
              <Badge appearance="outline" color="informative">Last 7 days</Badge>
            </div>

            <div className={ls.trendGrid}>
              {TRUST_TREND.map((d) => (
                <div key={d.day} style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS, alignItems: 'center' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '80px', gap: '2px' }}>
                    <div
                      className={ls.trendBar}
                      style={{
                        width: '24px',
                        height: `${Math.round((d.verified / maxTrustVerified) * 70)}px`,
                        backgroundColor: governanceTokens.colorStatusSuccess,
                      }}
                    />
                    <div
                      className={ls.trendBar}
                      style={{
                        width: '24px',
                        height: `${Math.max(Math.round((d.unsupported / maxTrustVerified) * 70), 3)}px`,
                        backgroundColor: governanceTokens.colorStatusWarning,
                      }}
                    />
                  </div>
                  <Caption1>{d.day}</Caption1>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: tokens.spacingHorizontalL, justifyContent: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalXS }}>
                <div style={{ width: '12px', height: '12px', borderRadius: '2px', backgroundColor: governanceTokens.colorStatusSuccess }} />
                <Caption1>Verified</Caption1>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalXS }}>
                <div style={{ width: '12px', height: '12px', borderRadius: '2px', backgroundColor: governanceTokens.colorStatusWarning }} />
                <Caption1>Unsupported</Caption1>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* F. Simulation Queue */}
      <Card>
        <div className={ps.cardBody}>
          <div className={ps.sectionHeader}>
            <Title2>Simulation Queue</Title2>
            <Button
              appearance="subtle"
              size="small"
              icon={<BeakerRegular />}
              onClick={() => navigate('/ai-governance/simulations')}
            >
              View All
            </Button>
          </div>

          <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
            AI-suggested policy changes awaiting simulation and human review.
          </Body2>

          <div className={ps.rowList}>
            {SIMULATION_QUEUE.map((sim) => (
              <div key={sim.id} className={ps.rowItem}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS, flex: 1 }}>
                  <Body1 style={{ fontWeight: 600 }}>{sim.description}</Body1>
                  <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Source: {sim.source}</Caption1>
                </div>
                <div style={{ display: 'flex', gap: tokens.spacingHorizontalS, alignItems: 'center', flexShrink: 0 }}>
                  <Badge appearance="tint" color={simulationStatusColor(sim.status)} size="small">
                    {sim.status}
                  </Badge>
                  <Badge appearance="outline" size="small" icon={<BrainCircuitRegular />}>
                    AI Recommendation
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Card>
    </div>
  );
};
